// src/pages/Notifications.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, markNotificationsAsRead, deleteNotification } from "../api/notifications";
import "./Notifications.css";

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all"); // all, unread
  const [loading, setLoading] = useState(true);

  const loadNotifications = React.useCallback(async () => {
    try {
      setLoading(true);
      const unreadOnly = filter === "unread";
      const data = await getNotifications(unreadOnly, 0, 100);
      setNotifications(data);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAsRead = React.useCallback(async (notificationId) => {
    try {
      await markNotificationsAsRead([notificationId]);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  }, [notifications]);

  const handleMarkAllAsRead = React.useCallback(async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      if (unreadIds.length === 0) return;
      
      await markNotificationsAsRead(unreadIds);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }, [notifications]);

  const handleDelete = React.useCallback(async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  }, [notifications]);

  const handleNotificationClick = React.useCallback((notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    const { notification_type } = notification;
    
    switch (notification_type) {
      case "partner_request":
      case "partner_accepted":
      case "partner_rejected":
        navigate("/find-partner");
        break;
      case "agency_query":
      case "agency_response":
      case "agency_accepted":
      case "agency_rejected":
      case "agency_request":
        navigate("/agencies");
        break;
      case "rating_received":
        navigate("/profile");
        break;
      case "new_destination_match":
        navigate("/find-partner");
        break;
      default:
        break;
    }
  }, [handleMarkAsRead, navigate]);

  function getNotificationIcon(type) {
    const icons = {
      partner_request: "👥",
      partner_accepted: "✅",
      partner_rejected: "❌",
      agency_query: "💼",
      agency_response: "📨",
      agency_accepted: "✅",
      agency_rejected: "❌",
      agency_request: "🏢",
      new_destination_match: "✈️",
      rating_received: "⭐"
    };
    return icons[type] || "🔔";
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    
    // Reset time to midnight for accurate day comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffTime = nowOnly - dateOnly;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="notifications-header">
        <div className="container">
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">
            Stay updated with your travel connections and activities
          </p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="notifications-controls">
        <div className="container">
          <div className="controls-wrapper">
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filter === "all" ? "active" : ""}`}
                onClick={() => setFilter("all")}
              >
                All
                <span className="filter-count">{notifications.length}</span>
              </button>
              <button
                className={`filter-btn ${filter === "unread" ? "active" : ""}`}
                onClick={() => setFilter("unread")}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="filter-count unread">{unreadCount}</span>
                )}
              </button>
            </div>
            {unreadCount > 0 && (
              <button
                className="mark-all-btn"
                onClick={handleMarkAllAsRead}
              >
                ✓ Mark all as read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="notifications-content">
        <div className="container">
          {loading ? (
            <div className="notifications-loading">
              <div className="loading-spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length > 0 ? (
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-card ${!notification.is_read ? "unread" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon-large">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="notification-body">
                    <div className="notification-title-large">
                      {notification.title}
                    </div>
                    <div className="notification-message-large">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-date">
                        {formatDate(notification.created_at)}
                      </span>
                      {!notification.is_read && (
                        <span className="unread-dot">●</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="notification-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                    title="Delete notification"
                  >
                    <span className="delete-icon">🗑️</span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="notifications-empty">
              <div className="empty-icon-large">
                {filter === "unread" ? "✅" : "🔔"}
              </div>
              <h2>
                {filter === "unread" ? "All caught up!" : "No notifications yet"}
              </h2>
              <p>
                {filter === "unread" 
                  ? "You have no unread notifications. Great job staying on top of things!"
                  : "When you receive notifications about partner requests, agency responses, or other activities, they'll appear here."}
              </p>
              {filter === "unread" && (
                <button
                  className="show-all-btn"
                  onClick={() => setFilter("all")}
                >
                  Show All Notifications
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
